import { defineComponent } from 'vue'
import ActiveUserMixin from './activeUserMixin'
import OwnerDetailMixin from './ownerDetailMixin'
import RepoDetailMixin from './repoDetailMixin'

let installWindow: Window | null = null

export default defineComponent({
  name: 'InstallAutofixMixin',

  mixins: [RepoDetailMixin, OwnerDetailMixin, ActiveUserMixin],

  data() {
    return {
      installing: false,
      failTimeout: null as ReturnType<typeof setTimeout> | null,
      popUpTimer: null as ReturnType<typeof setTimeout> | null,
      pollTimer: null as ReturnType<typeof setTimeout> | null,
      close: null as (() => void) | null
    }
  },

  computed: {
    isRepoLevel(): boolean {
      return Boolean(this.$route.params.repo)
    },

    ownerParams(): { login: string; provider: string } {
      return { login: this.activeOwner, provider: this.activeProvider }
    }
  },

  beforeDestroy() {
    this.clearTimers()
    this.$socket.$off('autofix-installation-complete')
  },

  async fetch(): Promise<void> {
    if (this.isRepoLevel) {
      await this.fetchRepoDetails(this.baseRouteParams)
    } else {
      await this.fetchOwnerDetails(this.ownerParams)
    }
  },

  methods: {
    async refetchRepo(): Promise<void> {
      try {
        await this.fetchBasicRepoDetails({
          ...this.baseRouteParams,
          refetch: true
        })
      } catch (e) {
        this.$logErrorAndToast(e as Error, 'An error occurred while fetching repository details.')
      }
    },

    async refetchOwner(): Promise<void> {
      await this.fetchOwnerDetails({
        ...this.ownerParams,
        refetch: true
      })
    },

    clearTimers(): void {
      if (this.failTimeout) clearTimeout(this.failTimeout)
      if (this.popUpTimer) clearInterval(this.popUpTimer)
      if (this.pollTimer) clearInterval(this.pollTimer)
    },

    async finish(): Promise<void> {
      if (this.isRepoLevel) {
        await this.refetchRepo()
      }

      this.clearTimers()
      this.$socket.$off('autofix-installation-complete')

      if (this.close && typeof this.close === 'function') {
        this.close()
      } else {
        this.$emit('close')
      }
      this.installing = false
    },

    async success(): Promise<void> {
      this.clearTimers()
      installWindow?.postMessage('close', window.location.origin)
      this.$toast.success('Successfully installed Autofix app')
      await this.finish()
    },

    async fail(): Promise<void> {
      this.clearTimers()
      this.$toast.danger(
        'Autofix app was not installed. It could take a while to reflect these changes on DeepSource, check back in a few minutes '
      )
      await this.finish()
    },

    async openAutofixInstallationUrl({
      close,
      forceOwnerLevel
    }: {
      close?: () => void
      forceOwnerLevel?: boolean
    }): Promise<void> {
      this.close = close
        ? close
        : () => {
            /* intentionally left empty: no-op default close handler */
          }
      this.installing = true
      const isRepoLevel = forceOwnerLevel ? false : this.isRepoLevel

      let installationUrl = isRepoLevel
        ? this.repository.autofixInstallationUrl
        : this.owner.autofixInstallationUrl

      if (!installationUrl) {
        await (isRepoLevel ? this.refetchRepo() : this.refetchOwner())

        installationUrl = isRepoLevel
          ? this.repository.autofixInstallationUrl
          : this.owner.autofixInstallationUrl
      }

      if (installationUrl) {
        installWindow = window.open(installationUrl, '', 'resizable=no,width=1000,height=600')

        this.popUpTimer = setInterval(() => {
          if (installWindow?.closed) {
            this.failTimeout = setTimeout(() => {
              this.fail().catch((e) => {
                this.$logErrorAndToast(e)
              })
            }, 5000)
            if (this.popUpTimer) clearInterval(this.popUpTimer)
            if (this.pollTimer) clearInterval(this.pollTimer)
          }
        }, 1000)

        if (isRepoLevel) {
          this.pollTimer = setInterval(() => {
            this.refetchRepo().catch((e) => {
              this.$logErrorAndToast(e)
            })
            if (this.repository.isAutofixEnabled) {
              if (this.pollTimer) clearInterval(this.pollTimer)
              this.success().catch((e) => {
                this.$logErrorAndToast(e)
              })
            }
          }, 1200)
        } else {
          this.pollTimer = setInterval(() => {
            this.refetchOwner().catch((e) => {
              this.$logErrorAndToast(e)
            })
            if (this.owner.isAutofixEnabled) {
              if (this.pollTimer) clearInterval(this.pollTimer)
              this.success().catch((e) => {
                this.$logErrorAndToast(e)
              })
            }
          }, 1200)
        }
        this.$socket.$on('autofix-installation-complete', async () => {
          await this.success()
        })
      }
    }
  }
})
